import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReactECharts from 'echarts-for-react';

interface HSCodeBalanceMap {
  [key: string]: number;
}

interface HSCodeTradeMap {
  [key: string]: any;
}

interface RCAByCode {
  [key: string]: number;
}

interface ProdcomValueByCode {
  [key: string]: number;
}

interface ProdcomTooltipByCode {
  [code: string]: {
    description?: string;
    indicatorLabel?: string;
    valueText?: string;
    aggregatedItemNames?: string[];
  };
}

interface ProdcomTooltipByNodeKey {
  [nodeKey: string]: {
    description?: string;
    indicatorLabel?: string;
    valueText?: string;
    aggregatedItemNames?: string[];
  };
}

interface HSCodeDescriptionsByCode {
  [code: string]: {
    description_2?: string | null;
    description_4?: string | null;
    description_6?: string | null;
    section?: string | null;
    section_description?: string | null;
  };
}

interface SankeyData {
  nodes: Array<{
    name: any;
    value?: number;
    itemStyle?: { color: string };
    depth?: number;
    label?: any;
    nodeData: {
      type: any;
      properties: any;
      elementId: any;
      n_iteration: any;
    };
  }>;
  links: any[];
}

interface SankeyDiagramProps {
  data: SankeyData | null;
  loading: boolean;
  error: string | null;
  onNodeClick?: (node: any) => void;
  title?: string;
  height?: number;
  maxLabelChars?: number;
  maxLabelWidthPx?: number;
  applyAbbreviations?: boolean;
  minFlowThreshold?: number;
  rootElementId?: string;
  requireRootConnectivity?: boolean;
  clearFocusTrigger?: number;
  enablePersistentFocus?: boolean;
  hsCodeBalanceMap?: HSCodeBalanceMap;
  hsCodeTradeMap?: HSCodeTradeMap;
  rcaByCode?: RCAByCode;
  prodcomValueByCode?: ProdcomValueByCode;
  prodcomTooltipByCode?: ProdcomTooltipByCode;
  prodcomTooltipByNodeKey?: ProdcomTooltipByNodeKey;
  /** Optional direct node-name -> numeric value map for Sankey coloring */
  nodeValueByName?: Record<string, number>;
  /** Optional node-name -> mapped companies with metric values (for tooltip rendering) */
  nodeCompanyValuesByName?: Record<string, Array<{ companyName: string; domain?: string; value: number }>>;
  /** Optional node-name -> mapped companies (even when no metric values are accessible) */
  nodeMappedCompaniesByName?: Record<string, Array<{ companyName: string; domain?: string }>>;
  /** Optional node-name -> true when companies exist but no financial metric is accessible */
  nodeNoAccessibleFinancialDataByName?: Record<string, boolean>;
  /** When false, tooltip lists mapped companies without metric values */
  showCompanyValuesInTooltip?: boolean;
  /** Color scale for nodeValueByName coloring */
  nodeValueColorScale?: 'linear' | 'log';
  /** Optional color for nodes/links without mapped nodeValueByName values */
  nodeValueMissingColor?: string;
  /** Force basic Sankey tooltips even without HS/PRODCOM metadata maps */
  enableBasicTooltip?: boolean;
  /** Optional label shown for basic node metric values */
  tooltipValueLabel?: string;
  /** When true, hide numeric values in basic tooltips */
  anonymizeMetricValues?: boolean;
  codeLabelContext?: 'auto' | 'hs' | 'prodcom';
  prodcomColorScale?: 'linear' | 'log';
  colorMode?: 'balance' | 'rca' | 'exports' | 'imports';
  rcaUpper?: number;
  /**
   * Optional cap for exports/imports coloring.
   * If provided and > 0, used as the max value for log scaling (values are in USD thousands).
   * Otherwise the cap is computed from the HS codes present in the current Sankey.
   */
  tradeColorCap?: number;
  rootGreyColor?: string;
  /** When true, depth-0 root nodes are forced to neutral grey */
  forceRootGrey?: boolean;
  defaultHSColor?: string;
  colorHSWhenNoBalance?: boolean;
  legend?: React.ReactNode;
  /** Optional mapping of HS6 code -> HS2/HS4/HS6 descriptions for richer tooltips */
  hsCodeDescriptionsByCode?: HSCodeDescriptionsByCode;
  /** Value chain info text to show on info icon hover */
  valueChainInfo?: string | null;
  /** Value chain shortcomings text to show on warning icon hover */
  valueChainShortcomings?: string | null;
}

const SankeyDiagram: React.FC<SankeyDiagramProps> = ({ 
  data, 
  loading, 
  error, 
  onNodeClick, 
  title = "Sankey Diagram",
  height = 600,
  maxLabelChars = 26,
  maxLabelWidthPx = 180,
  applyAbbreviations = true,
  minFlowThreshold,
  rootElementId,
  requireRootConnectivity = false,
  clearFocusTrigger = 0,
  enablePersistentFocus = false,
  hsCodeBalanceMap = {},
  hsCodeTradeMap = {},
  rcaByCode = {},
  prodcomValueByCode = {},
  prodcomTooltipByCode = {},
  prodcomTooltipByNodeKey = {},
  nodeValueByName = {},
  nodeCompanyValuesByName = {},
  nodeMappedCompaniesByName = {},
  nodeNoAccessibleFinancialDataByName = {},
  showCompanyValuesInTooltip = true,
  nodeValueColorScale = 'linear',
  nodeValueMissingColor,
  enableBasicTooltip = false,
  tooltipValueLabel = 'Value',
  anonymizeMetricValues = false,
  codeLabelContext = 'auto',
  prodcomColorScale = 'linear',
  colorMode = 'balance',
  rcaUpper = 10,
  tradeColorCap,
  rootGreyColor = '#9e9e9e',
  forceRootGrey = true,
  defaultHSColor = '#42a5f5',
  colorHSWhenNoBalance = true,
  legend = null,
  hsCodeDescriptionsByCode = {},
  valueChainInfo = null,
  valueChainShortcomings = null
}) => {
  const theme = useTheme();
  const [focusedName, setFocusedName] = useState<string | null>(null);
  const chartRef = useRef<any>(null);

  // Abbreviate common long terms and strip parentheticals for more compact labels
  const abbreviateName = useMemo(() => (name: string) => {
    if (!name) return '';
    let result = String(name);
    // Remove parenthetical text which is often verbose
    result = result.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
    const replacements: [RegExp, string][] = [
      [/Photovoltaic/gi, 'PV'],
      [/Silicon/gi, 'Si'],
      [/Grid-Connected/gi, 'Grid-Conn.'],
      [/Stand-Alone/gi, 'Off-Grid'],
      [/Battery Storage Bank/gi, 'Battery Bank']
    ];
    replacements.forEach(([pattern, shortText]) => {
      result = result.replace(pattern, shortText);
    });
    return result;
  }, []);

  const formatLabel = useMemo(() => (name) => {
    const base = applyAbbreviations ? abbreviateName(name) : name;
    if (typeof maxLabelChars === 'number' && maxLabelChars > 0 && base.length > maxLabelChars) {
      return base.slice(0, Math.max(0, maxLabelChars - 1)) + '…';
    }
    return base;
  }, [applyAbbreviations, maxLabelChars, abbreviateName]);

  const getNodeDisplayLabel = useMemo(() => (params: any) => {
    const p = params?.data?.nodeData?.properties || {};
    const nodeType = String(params?.data?.nodeData?.type || '');
    const isGroupedNode = nodeType === 'Grouped';
    const iteration = Number(params?.data?.nodeData?.n_iteration ?? p?.n_iteration);
    const isRootIteration = Number.isFinite(iteration) && iteration === 0;
    const groupedName = String(p?.name ?? params?.name ?? '').trim();
    const groupedNameWithoutPrefix = groupedName.replace(/^\s*\d+\s*:\s*/, '').trim();

    // Keep canonical node names on non-grouped Sankeys (e.g. Systems / Technology Types).
    // Only grouped HS/PRODCOM views should prefer code descriptions as display labels.
    const raw = isGroupedNode
      ? (
        codeLabelContext === 'prodcom'
          ? (p.display_label ?? p.prodcom_description ?? (isRootIteration ? groupedNameWithoutPrefix : null))
          : (codeLabelContext === 'hs'
            ? (p.display_label ?? p.hs_code_description ?? null)
            : (p.display_label ?? p.prodcom_description ?? p.hs_code_description ?? null))
      )
      : (p.display_label ?? p.name ?? null);
    const desc = Array.isArray(raw) ? raw[0] : raw;
    if (typeof desc === 'string' && desc.trim()) return desc.trim();
    if (isGroupedNode && (codeLabelContext === 'prodcom' || codeLabelContext === 'hs')) return '';
    return params?.name || '';
  }, [codeLabelContext]);

  // Extract 6-digit HS code from a node
  const extractHSCodeFromNode = (node) => {
    const sixDigit = /\b(\d{6})\b/;
    const p = node?.nodeData?.properties || {};
    const hs = p.hs_code;
    if (Array.isArray(hs)) {
      for (const v of hs) { if (typeof v === 'string') { const m = v.match(sixDigit); if (m) return m[1]; } }
    } else if (typeof hs === 'string') {
      const m = hs.match(sixDigit); if (m) return m[1];
    }
    if (typeof p.name === 'string') { const m = p.name.match(sixDigit); if (m) return m[1]; }
    if (typeof node?.name === 'string') { const m = node.name.match(sixDigit); if (m) return m[1]; }
    return null;
  };

  // Extract PRODCOM code from grouped/member node data
  const extractProdcomCodeFromNode = (node) => {
    const p = node?.nodeData?.properties || {};
    const direct = p?.prodcom_selected_code;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    const members = Array.isArray(p?.members) ? p.members : [];
    for (const m of members) {
      const code = m?.properties?.prodcom_selected_code;
      if (typeof code === 'string' && code.trim()) return code.trim();
    }

    const parseGroupedName = (value: any): string | null => {
      if (typeof value !== 'string') return null;
      const match = value.match(/^\s*\d+\s*:\s*(.+?)\s*$/);
      const code = (match?.[1] || '').trim();
      if (!code || code === '(no-prodcom)') return null;
      return code;
    };

    return parseGroupedName(p?.name) || parseGroupedName(node?.name) || null;
  };

  const truncate = useMemo(() => (s: string, n: number) => {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    return t.length > n ? t.slice(0, Math.max(0, n - 1)) + '…' : t;
  }, []);

  // If a label looks like MOSTLY ALL CAPS, render it in sentence case for a calmer look.
  const softenAllCaps = useMemo(() => (s: string) => {
    const t = (s || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    const letters = t.replace(/[^A-Za-z]+/g, '');
    if (letters.length < 6) return t;
    const upper = (letters.match(/[A-Z]/g) || []).length;
    const ratio = letters.length ? upper / letters.length : 0;
    if (ratio >= 0.85) {
      const lower = t.toLowerCase();
      return lower ? lower[0].toUpperCase() + lower.slice(1) : lower;
    }
    return t;
  }, []);

  const normalizeSectionCode = useMemo(() => (v: string) => {
    const t = (v || '').replace(/\s+/g, ' ').trim();
    if (!t) return '';
    const stripped = t.replace(/^section\b\s*/i, '').trim();
    return stripped || t;
  }, []);

  const fmt2 = useMemo(() => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const hasHsDescMap = useMemo(() => {
    try {
      return hsCodeDescriptionsByCode && Object.keys(hsCodeDescriptionsByCode).length > 0;
    } catch {
      return false;
    }
  }, [hsCodeDescriptionsByCode]);

  const hasProdcomTooltipMap = useMemo(() => {
    try {
      return prodcomTooltipByCode && Object.keys(prodcomTooltipByCode).length > 0;
    } catch {
      return false;
    }
  }, [prodcomTooltipByCode]);
  const hasProdcomTooltipNodeMap = useMemo(() => {
    try {
      return prodcomTooltipByNodeKey && Object.keys(prodcomTooltipByNodeKey).length > 0;
    } catch {
      return false;
    }
  }, [prodcomTooltipByNodeKey]);


  // Optionally filter links by min flow and keep only participating nodes
  const processed = (() => {
    if (!data || !data.nodes || !data.links) return { nodes: [], links: [] };
    const links = Array.isArray(data.links) ? data.links.slice() : [];
    let nodes = Array.isArray(data.nodes) ? data.nodes.slice() : [];

    // Align nodes with the same n_iteration (depth) on the same vertical column
    const iterations = nodes
      .map(n => n?.nodeData?.n_iteration)
      .filter(v => Number.isFinite(v));
    if (iterations.length > 0) {
      const minIt = Math.min(...iterations);
      nodes = nodes.map(n => {
        const it = n?.nodeData?.n_iteration;
        if (Number.isFinite(it)) {
          return { ...n, depth: it - minIt };
        }
        return n;
      });
    }

    const filteredLinks = typeof minFlowThreshold === 'number'
      ? links.filter(link => (link?.value ?? 0) >= minFlowThreshold)
      : links;

    const usedNodeNames = new Set();
    filteredLinks.forEach(l => {
      if (l && l.source) usedNodeNames.add(l.source);
      if (l && l.target) usedNodeNames.add(l.target);
    });

    let filteredNodes = nodes.filter(n => usedNodeNames.size === 0 || usedNodeNames.has(n.name));

    // Optional: enforce connectivity to root
    if (requireRootConnectivity && rootElementId) {
      // Map elementId -> name to resolve the root node name
      const elementIdToName = new Map();
      nodes.forEach(n => {
        const eid = n?.nodeData?.elementId;
        if (eid) elementIdToName.set(eid, n.name);
      });
      const rootName = elementIdToName.get(rootElementId);
      if (rootName) {
        // Build undirected adjacency from filteredLinks
        const adj = new Map();
        const ensure = (k) => { if (!adj.has(k)) adj.set(k, new Set()); return adj.get(k); };
        filteredLinks.forEach(l => {
          ensure(l.source).add(l.target);
          ensure(l.target).add(l.source);
        });

        // BFS from rootName
        const visited = new Set();
        const queue = [];
        if (adj.has(rootName)) {
          visited.add(rootName);
          queue.push(rootName);
          while (queue.length) {
            const cur = queue.shift();
            const nbrs = adj.get(cur) || new Set();
            nbrs.forEach(nbr => {
              if (!visited.has(nbr)) {
                visited.add(nbr);
                queue.push(nbr);
              }
            });
          }
        } else {
          // If root has no adjacency after filtering, everything should disappear
          visited.add(rootName);
        }

        // Keep only nodes/links connected to rootName
        filteredNodes = filteredNodes.filter(n => visited.has(n.name));
        const connectedNameSet = new Set(filteredNodes.map(n => n.name));
        const prunedLinks = filteredLinks.filter(l => connectedNameSet.has(l.source) && connectedNameSet.has(l.target));

        // Replace filteredLinks with prunedLinks for subsequent sizing
        filteredLinks.length = 0;
        prunedLinks.forEach(l => filteredLinks.push(l));
      }
    }

    // Compute node sizing from links (greater of incoming/outgoing), when we have links
    if (filteredLinks.length > 0) {
      const incoming = new Map();
      const outgoing = new Map();
      filteredNodes.forEach(n => { incoming.set(n.name, 0); outgoing.set(n.name, 0); });
      filteredLinks.forEach(l => {
        incoming.set(l.target, (incoming.get(l.target) || 0) + (l.value || 0));
        outgoing.set(l.source, (outgoing.get(l.source) || 0) + (l.value || 0));
      });
      filteredNodes.forEach(n => {
        const inc = incoming.get(n.name) || 0;
        const out = outgoing.get(n.name) || 0;
        n.value = Math.max(inc, out);
      });
    }

    return { nodes: filteredNodes, links: filteredLinks };
  })();

  // Create adjacency sets for persistent focus styling
  const { styledNodes, styledLinks } = useMemo(() => {
    let nodes = processed.nodes || [];
    let links = processed.links || [];

    // Apply HS-code-based coloring when provided
    const sixDigit = /\b(\d{6})\b/;
    const lerp = (a, b, t) => a + (b - a) * t;
    const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
    const mix = (c1, c2, t) => {
      const h = (c) => [parseInt(c.slice(1,3),16), parseInt(c.slice(3,5),16), parseInt(c.slice(5,7),16)];
      const [r1,g1,b1] = h(c1); const [r2,g2,b2] = h(c2);
      const r = Math.round(lerp(r1,r2,t));
      const g = Math.round(lerp(g1,g2,t));
      const b = Math.round(lerp(b1,b2,t));
      const toHex = (v)=> v.toString(16).padStart(2,'0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };
    const colorForNorm = (n) => {
      const nn = clamp(Number(n) || 0, -1, 1);
      if (nn < 0) {
        const t = nn + 1; // 0..1 from red to yellow
        return mix('#d32f2f', '#fbc02d', t);
      } else {
        const t = nn; // 0..1 from yellow to green
        return mix('#fbc02d', '#2e7d32', t);
      }
    };
    const viridis = (tRaw: number) => {
      const t = clamp(Number(tRaw) || 0, 0, 1);
      const stops: Array<[number, string]> = [
        // Reversed Viridis: yellow -> blue/purple
        [0.0, '#fde725'],
        [0.25, '#5ec962'],
        [0.5, '#21918c'],
        [0.75, '#3b528b'],
        [1.0, '#440154'],
      ];
      for (let i = 0; i < stops.length - 1; i++) {
        const [t0, c0] = stops[i];
        const [t1, c1] = stops[i + 1];
        if (t <= t1) {
          const u = (t1 - t0) > 0 ? (t - t0) / (t1 - t0) : 0;
          return mix(c0, c1, clamp(u, 0, 1));
        }
      }
      return stops[stops.length - 1][1];
    };
    const colorForTrade = (value: number, cap: number, scale: 'linear' | 'log' = 'linear') => {
      const v = Math.max(0, Number(value) || 0);
      const c = Math.max(0, Number(cap) || 0);
      // Reversed Viridis (sequential), with linear or log1p normalization.
      const t = c > 0
        ? (scale === 'log'
          ? (Math.log1p(v) / Math.log1p(c))
          : (v / c))
        : 0;
      return viridis(t);
    };

    const nodeNameToColor = new Map();
    const rcaToNorm = (rca) => {
      if (rca == null) return 0;
      const eps = 1e-6;
      const base = Number(rcaUpper) > 1 ? Number(rcaUpper) : 10;
      const n = Math.log(Math.max(Number(rca), eps)) / Math.log(base);
      return clamp(n, -1, 1);
    };

    const hasProdcomColorMap = prodcomValueByCode && Object.keys(prodcomValueByCode).length > 0;
    const hasNodeValueMap = nodeValueByName && Object.keys(nodeValueByName).length > 0;
    const isTradeMode = !hasProdcomColorMap && (colorMode === 'exports' || colorMode === 'imports');
    const tradeField: 'exports' | 'imports' = colorMode === 'imports' ? 'imports' : 'exports';
    let computedTradeCap = (Number.isFinite(tradeColorCap as any) && Number(tradeColorCap) > 0)
      ? Number(tradeColorCap)
      : 0;

    if (hasNodeValueMap && computedTradeCap <= 0) {
      nodes.forEach((n) => {
        const val = Number((nodeValueByName as any)?.[String(n?.name ?? '')]);
        if (Number.isFinite(val)) computedTradeCap = Math.max(computedTradeCap, val);
      });
    } else if (hasProdcomColorMap && computedTradeCap <= 0) {
      nodes.forEach((n) => {
        const code = extractProdcomCodeFromNode(n);
        if (!code) return;
        const val = Number((prodcomValueByCode as any)?.[code]);
        if (Number.isFinite(val)) computedTradeCap = Math.max(computedTradeCap, val);
      });
    } else if (isTradeMode && computedTradeCap <= 0) {
      // Compute cap from HS codes present in the current Sankey
      nodes.forEach((n) => {
        let code: string | null = null;
        const p = n?.nodeData?.properties || {};
        const hs = p.hs_code;
        if (Array.isArray(hs)) {
          for (const v of hs) { if (typeof v === 'string') { const m = v.match(sixDigit); if (m) { code = m[1]; break; } } }
        } else if (typeof hs === 'string') {
          const m = hs.match(sixDigit); if (m) code = m[1];
        }
        if (!code && typeof p.name === 'string') { const m = p.name.match(sixDigit); if (m) code = m[1]; }
        if (!code && typeof n.name === 'string') { const m = n.name.match(sixDigit); if (m) code = m[1]; }
        if (!code) return;

        const row = (hsCodeTradeMap as any)?.[code];
        const val = Number(row?.[tradeField]) || 0;
        if (Number.isFinite(val)) computedTradeCap = Math.max(computedTradeCap, val);
      });
    }

    nodes.forEach(n => {
      let code = null;
      const p = n?.nodeData?.properties || {};
      const hs = p.hs_code;
      if (Array.isArray(hs)) {
        for (const v of hs) { if (typeof v === 'string') { const m = v.match(sixDigit); if (m) { code = m[1]; break; } } }
      } else if (typeof hs === 'string') {
        const m = hs.match(sixDigit); if (m) code = m[1];
      }
      if (!code && typeof p.name === 'string') { const m = p.name.match(sixDigit); if (m) code = m[1]; }
      if (!code && typeof n.name === 'string') { const m = n.name.match(sixDigit); if (m) code = m[1]; }
      if (hasNodeValueMap) {
        if (Object.prototype.hasOwnProperty.call(nodeValueByName, n.name)) {
          const v = Number((nodeValueByName as any)?.[n.name]) || 0;
          nodeNameToColor.set(n.name, colorForTrade(v, computedTradeCap, nodeValueColorScale));
        }
      } else if (hasProdcomColorMap) {
        const prodcomCode = extractProdcomCodeFromNode(n);
        if (prodcomCode && Object.prototype.hasOwnProperty.call(prodcomValueByCode, prodcomCode)) {
          const v = Number((prodcomValueByCode as any)?.[prodcomCode]) || 0;
          nodeNameToColor.set(n.name, colorForTrade(v, computedTradeCap, prodcomColorScale));
        }
      } else if (isTradeMode) {
        if (!code) return;
        const row = (hsCodeTradeMap as any)?.[code];
        if (row && Object.prototype.hasOwnProperty.call(row, tradeField)) {
          const v = Number(row?.[tradeField]) || 0;
          nodeNameToColor.set(n.name, colorForTrade(v, computedTradeCap, 'linear'));
        }
      } else if (colorMode === 'rca') {
        if (!code) return;
        if (Object.prototype.hasOwnProperty.call(rcaByCode, code)) {
          const norm = rcaToNorm(rcaByCode[code]);
          nodeNameToColor.set(n.name, colorForNorm(norm));
        }
      } else {
        if (!code) return;
        if (Object.prototype.hasOwnProperty.call(hsCodeBalanceMap, code)) {
          nodeNameToColor.set(n.name, colorForNorm(hsCodeBalanceMap[code]));
        }
      }
    });

    // Identify root nodes (depth === 0) and force a neutral grey color
    const rootNameSet = new Set(nodes.filter(n => typeof n.depth === 'number' && n.depth === 0).map(n => n.name));
    const referenceMap =
      (colorMode === 'rca') ? rcaByCode :
      (colorMode === 'balance') ? hsCodeBalanceMap :
      hsCodeTradeMap;
    const shouldColorHSByDefault = colorHSWhenNoBalance && (!referenceMap || Object.keys(referenceMap).length === 0);
    const hsNodeNameSet = new Set();
    if (shouldColorHSByDefault) {
      nodes.forEach(n => { if (extractHSCodeFromNode(n)) hsNodeNameSet.add(n.name); });
    }

    if (nodeNameToColor.size > 0 || (forceRootGrey && rootNameSet.size > 0)) {
      nodes = nodes.map(n => {
        const isRoot = rootNameSet.has(n.name);
        const hasHS = hsNodeNameSet.has(n.name);
        const hasNodeMetricValue = hasNodeValueMap && Object.prototype.hasOwnProperty.call(nodeValueByName, n.name);
        const color = (isRoot && forceRootGrey)
          ? rootGreyColor
          : (nodeNameToColor.get(n.name)
            || (hasNodeValueMap && !hasNodeMetricValue ? nodeValueMissingColor : undefined)
            || (shouldColorHSByDefault && hasHS ? defaultHSColor : (n.itemStyle && n.itemStyle.color)));
        return {
          ...n,
          itemStyle: {
            ...(n.itemStyle || {}),
            color
          }
        };
      });
      links = links.map(l => ({
        ...l,
        lineStyle: {
          ...(l.lineStyle || {}),
          color: nodeNameToColor.get(l.source)
            || (hasNodeValueMap && nodeValueMissingColor && !Object.prototype.hasOwnProperty.call(nodeValueByName, l.source) ? nodeValueMissingColor : undefined)
            || (shouldColorHSByDefault && hsNodeNameSet.has(l.source) ? defaultHSColor : (l.lineStyle && l.lineStyle.color) || 'source')
        }
      }));
    }

    // De-overlap labels for the specific 1-to-1 root case.
    const depth0Nodes = nodes.filter((n: any) => Number(n?.depth) === 0);
    const depth1Nodes = nodes.filter((n: any) => Number(n?.depth) === 1);
    if (depth0Nodes.length === 1 && depth1Nodes.length === 1) {
      const rootNode = depth0Nodes[0];
      const nextNode = depth1Nodes[0];
      const isDirectlyConnected = links.some((l: any) =>
        (String(l?.source) === String(rootNode.name) && String(l?.target) === String(nextNode.name)) ||
        (String(l?.source) === String(nextNode.name) && String(l?.target) === String(rootNode.name))
      );

      if (isDirectlyConnected) {
        nodes = nodes.map((n: any) => {
          if (String(n.name) === String(rootNode.name)) {
            return {
              ...n,
              label: {
                ...(n.label || {}),
                offset: [0, -10],
              },
            };
          }
          if (String(n.name) === String(nextNode.name)) {
            return {
              ...n,
              label: {
                ...(n.label || {}),
                offset: [0, 10],
              },
            };
          }
          return n;
        });
      }
    }

    if (!enablePersistentFocus || !focusedName) {
      return { styledNodes: nodes, styledLinks: links };
    }

    const neighbors = new Set([focusedName]);
    links.forEach(l => {
      if (l.source === focusedName) neighbors.add(l.target);
      if (l.target === focusedName) neighbors.add(l.source);
    });

    const styledNodesLocal = nodes.map(n => ({
      ...n,
      itemStyle: {
        ...(n.itemStyle || {}),
        opacity: neighbors.has(n.name) ? 1 : 0.15
      },
      label: {
        ...(n.label || {}),
        opacity: neighbors.has(n.name) ? 1 : 0.3
      }
    }));

    const styledLinksLocal = links.map(l => ({
      ...l,
      lineStyle: {
        ...(l.lineStyle || {}),
        opacity: (l.source === focusedName || l.target === focusedName) ? 0.9 : 0.1
      }
    }));

    return { styledNodes: styledNodesLocal, styledLinks: styledLinksLocal };
  }, [processed.nodes, processed.links, focusedName, enablePersistentFocus, hsCodeBalanceMap, hsCodeTradeMap, rcaByCode, prodcomValueByCode, prodcomColorScale, nodeValueByName, nodeValueColorScale, nodeValueMissingColor, colorMode, rcaUpper, tradeColorCap, rootGreyColor, forceRootGrey, defaultHSColor, colorHSWhenNoBalance]);

  // Allow parent to clear persistent focus when trigger changes
  useEffect(() => {
    setFocusedName(null);
  }, [clearFocusTrigger]);

  // If persistent focus gets disabled, clear any existing focus
  useEffect(() => {
    if (!enablePersistentFocus && focusedName) {
      setFocusedName(null);
    }
  }, [enablePersistentFocus]);

  // Memoized event handlers to prevent re-renders
  const eventHandlers = useMemo(() => {
    const handle = (params) => {
      if (!onNodeClick) return;
      if (params?.dataType === 'node' || (params?.data && params?.data?.nodeData)) {
        onNodeClick(params);
        if (enablePersistentFocus) {
          setFocusedName(prev => (prev === params?.name ? null : params?.name));
        }
      }
    };
    return {
      click: handle,
      mouseup: handle,
      pointerup: handle
    } as any;
  }, [onNodeClick, enablePersistentFocus]);


  // Memoized ECharts option to avoid re-creating object on each render
  const option = useMemo(() => ({
    tooltip: {
      // Enable ECharts tooltip only when we have HS descriptions available.
      // This avoids changing tooltip behavior globally for other Sankey usages.
      show: hasHsDescMap || hasProdcomTooltipMap || hasProdcomTooltipNodeMap || enableBasicTooltip,
      trigger: 'item',
      confine: true,
      renderMode: 'html',
      extraCssText: [
        'max-width: min(92vw, 620px)',
        'max-height: min(60vh, 560px)',
        'overflow: auto',
        'white-space: normal',
        'word-break: break-word',
        'line-height: 1.25',
        'font-size: 13px',
        'padding: 10px 12px',
      ].join('; '),
      formatter: (params: any) => {
        try {
          // ECharts passes node object at params.data for nodes
          const node = params?.data;
          if (hasProdcomTooltipMap || hasProdcomTooltipNodeMap) {
            const prodcomCode = extractProdcomCodeFromNode(node);
            const groupedNodeKey = String(
              node?.nodeData?.properties?.name ??
              node?.name ??
              ''
            ).trim();
            const byNodeKey = groupedNodeKey ? (prodcomTooltipByNodeKey as any)?.[groupedNodeKey] : null;
            const byCode = prodcomCode ? (prodcomTooltipByCode as any)?.[prodcomCode] : null;
            const t = byNodeKey || byCode || null;
            if (t && (groupedNodeKey || prodcomCode)) {
              const description = t?.description ? truncate(String(t.description), 320) : '';
              const indicatorLabel = t?.indicatorLabel ? String(t.indicatorLabel) : '';
              const valueText = t?.valueText ? String(t.valueText) : '—';
              const itemNames = Array.isArray(t?.aggregatedItemNames) ? t.aggregatedItemNames.filter(Boolean).map((v: any) => String(v)) : [];
              const maxItemsToShow = 8;
              const shownItems = itemNames.slice(0, maxItemsToShow);
              const remaining = Math.max(0, itemNames.length - shownItems.length);
              const itemsLine = shownItems.length > 0
                ? `${shownItems.join(', ')}${remaining > 0 ? `, +${remaining} more` : ''}`
                : '';
              const lines = [
                `<div><b>${prodcomCode || groupedNodeKey}</b></div>`,
                description ? `<div>${description}</div>` : '',
                indicatorLabel ? `<div style="margin-top:8px"><b>${indicatorLabel}:</b> ${valueText}</div>` : '',
                itemsLine ? `<div style="margin-top:8px"><b>Aggregated items:</b> ${truncate(itemsLine, 520)}</div>` : '',
              ].filter(Boolean).join('');
              if (lines) return `<div>${lines}</div>`;
            }
          }
          const code6 = extractHSCodeFromNode(node);
          if (!code6) {
            if (enableBasicTooltip) {
              if (params?.dataType === 'edge') {
                const s = String(params?.data?.source || params?.source || '—');
                const t = String(params?.data?.target || params?.target || '—');
                const v = Number(params?.data?.value ?? params?.value ?? 0);
                const vText = Number.isFinite(v)
                  ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                  : String(params?.data?.value ?? params?.value ?? '—');
                const edgeVal = anonymizeMetricValues
                  ? `<span style="filter:blur(5px);user-select:none;display:inline-block;">${vText}</span>`
                  : vText;
                return `<div><b>${s} → ${t}</b><br/><span>${tooltipValueLabel}: ${edgeVal}</span></div>`;
              }
              const name = formatLabel(getNodeDisplayLabel(params));
              const nodeName = String(params?.data?.name ?? params?.name ?? '');
              const mapped = Number((nodeValueByName as any)?.[nodeName]);
              const hasMapped = Object.prototype.hasOwnProperty.call(nodeValueByName || {}, nodeName);
              const noAccessibleFinancialData = !!(nodeNoAccessibleFinancialDataByName as any)?.[nodeName];
              const rawValueText = hasMapped && Number.isFinite(mapped)
                ? mapped.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : (noAccessibleFinancialData ? 'No accessible financial data' : 'No mapped companies');
              const esc = (v: any) => String(v ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
              const blurIfNumeric = (text: string, shouldBlur: boolean) => (
                shouldBlur && text !== 'No mapped companies' && text !== 'No accessible financial data'
                  ? `<span style="filter:blur(5px);user-select:none;display:inline-block;">${esc(text)}</span>`
                  : esc(text)
              );
              const companyItems = Array.isArray((nodeCompanyValuesByName as any)?.[nodeName])
                ? (nodeCompanyValuesByName as any)[nodeName]
                : [];
              const mappedCompanies = Array.isArray((nodeMappedCompaniesByName as any)?.[nodeName])
                ? (nodeMappedCompaniesByName as any)[nodeName]
                : [];
              let companiesHtml = '';
              if (mappedCompanies.length > 0 || companyItems.length > 0) {
                const metricByDomain = new Map<string, number>();
                companyItems.forEach((it: any) => {
                  const d = String(it?.domain || '').trim();
                  if (!d) return;
                  const v = Number(it?.value);
                  if (!Number.isFinite(v)) return;
                  metricByDomain.set(d, v);
                });
                const mergedBase = mappedCompanies.length > 0
                  ? mappedCompanies
                  : companyItems.map((it: any) => ({ companyName: it?.companyName, domain: it?.domain }));
                const uniqueByDomain = new Map<string, { companyName: string; domain?: string }>();
                mergedBase.forEach((it: any) => {
                  const d = String(it?.domain || '').trim();
                  if (!d || uniqueByDomain.has(d)) return;
                  const cname = String(it?.companyName || d).trim() || d;
                  uniqueByDomain.set(d, { companyName: cname, domain: d });
                });
                const merged = Array.from(uniqueByDomain.values()).map((it) => {
                  const v = metricByDomain.get(String(it.domain || '').trim());
                  return { ...it, value: Number.isFinite(v as number) ? Number(v) : null };
                });
                merged.sort((a, b) => {
                  const av = a.value == null ? -Infinity : a.value;
                  const bv = b.value == null ? -Infinity : b.value;
                  return bv - av;
                });
                const top = merged.slice(0, 20);
                const lines = top.map((it: any) => {
                  if (!showCompanyValuesInTooltip) {
                    const cname = esc(it?.companyName || it?.domain || 'Company');
                    return `• ${cname}`;
                  }
                  const numericVal = Number(it?.value);
                  const hasVal = Number.isFinite(numericVal) && numericVal !== 0;
                  const rawValText = hasVal
                    ? numericVal.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : 'No accessible financial data';
                  const cname = esc(it?.companyName || it?.domain || 'Company');
                  const valText = anonymizeMetricValues
                    ? `<span style="filter:blur(5px);user-select:none;display:inline-block;">${esc(rawValText)}</span>`
                    : esc(rawValText);
                  return `• ${cname}: ${valText}`;
                });
                const more = merged.length > top.length ? `<div>… +${merged.length - top.length} more</div>` : '';
                companiesHtml = `<div style="margin-top:8px"><b>Mapped companies</b><br/>${lines.join('<br/>')}${more}</div>`;
              } else if (noAccessibleFinancialData) {
                companiesHtml = `<div style="margin-top:8px"><b>Mapped companies</b><br/>No accessible financial data</div>`;
              }
              return `<div><b>${esc(name)}</b><br/><span>${esc(tooltipValueLabel)}: ${blurIfNumeric(rawValueText, anonymizeMetricValues)}</span>${companiesHtml}</div>`;
            }
            return formatLabel(getNodeDisplayLabel(params));
          }
          const d = (hsCodeDescriptionsByCode as any)?.[code6] || {};
          const code2 = String(code6).slice(0, 2);
          const code4 = String(code6).slice(0, 4);
          const d2 = d?.description_2 ? truncate(String(d.description_2), 220) : '';
          const d4 = d?.description_4 ? truncate(String(d.description_4), 220) : '';
          const d6 = d?.description_6 ? truncate(String(d.description_6), 320) : '';
          const secRaw = d?.section != null ? String(d.section).trim() : '';
          const sec = secRaw ? normalizeSectionCode(secRaw) : '';
          const secDescRaw = d?.section_description != null ? String(d.section_description) : '';
          const secDesc = secDescRaw ? truncate(softenAllCaps(secDescRaw), 260) : '';
          const secLineHtml = (sec || secDesc)
            ? (sec
              ? `<b>Section ${sec}</b>${secDesc ? `: ${secDesc}` : ''}`
              : `Section ${secDesc}`)
            : '';
          const trade = (hsCodeTradeMap as any)?.[code6];
          const exp = Number(trade?.exports);
          const imp = Number(trade?.imports);
          const hasTrade = Number.isFinite(exp) || Number.isFinite(imp);
          const lines = [
            `<div><b>${code6}</b></div>`,
            (code6 && d6) ? `<div><b>${code6}:</b> ${d6}</div>` : '',
            (code4 && d4) ? `<div><b>${code4}:</b> ${d4}</div>` : '',
            (code2 && d2) ? `<div><b>${code2}:</b> ${d2}</div>` : '',
            secLineHtml ? `<div>${secLineHtml}</div>` : '',
            hasTrade ? `<div style="margin-top:8px"><b>Exports:</b> ${Number.isFinite(exp) ? fmt2.format(exp) : '—'} USD thousands</div>` : '',
            hasTrade ? `<div><b>Imports:</b> ${Number.isFinite(imp) ? fmt2.format(imp) : '—'} USD thousands</div>` : '',
          ].filter(Boolean).join('');
          return `<div>${lines}</div>`;
        } catch {
          return formatLabel(getNodeDisplayLabel(params));
        }
      }
    },
    series: [{
      type: 'sankey',
      layout: 'none',
      silent: false,
      data: styledNodes,
      links: styledLinks,
      blur: {},
      lineStyle: {
        color: 'source',
        curveness: 0.5
      },
      itemStyle: {
        borderWidth: 1,
        borderColor: theme.palette.divider
      },
      label: {
        position: 'right',
        color: theme.palette.text.primary,
        overflow: 'truncate',
        width: maxLabelWidthPx,
        formatter: (params) => formatLabel(getNodeDisplayLabel(params))
      },
      nodeAlign: 'justify',
      orient: 'horizontal',
      nodeWidth: 20,
      nodeGap: 10,
      layoutIterations: 32,
      focusNodeAdjacency: false,
      animation: false
    }]
  }), [styledNodes, styledLinks, theme.palette.text.primary, theme.palette.background.paper, theme.palette.divider, maxLabelWidthPx, formatLabel, getNodeDisplayLabel, hasHsDescMap, hasProdcomTooltipMap, hasProdcomTooltipNodeMap, enableBasicTooltip, tooltipValueLabel, anonymizeMetricValues, showCompanyValuesInTooltip, nodeValueByName, nodeCompanyValuesByName, nodeMappedCompaniesByName, nodeNoAccessibleFinancialDataByName, hsCodeDescriptionsByCode, hsCodeTradeMap, prodcomTooltipByCode, prodcomTooltipByNodeKey, truncate, fmt2]);

  const renderContent = () => {
    if (error) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Error: {error}</Typography>
        </Paper>
      );
    }
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (!data || !data.links || data.links.length === 0) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No data available to display.</Typography>
        </Paper>
      );
    }

    // option is computed at component scope via useMemo; just use it here
        return (
          <ReactECharts
            key={`sankey-${data?.nodes?.length || 0}-${data?.links?.length || 0}`}
            option={option}
            style={{ height: '100%', width: '100%' }}
            opts={{ renderer: 'canvas' }}
            notMerge={true}
            lazyUpdate={false}
            onEvents={eventHandlers}
            ref={chartRef}
          />
        );
  };

  return (
    <Paper elevation={2} sx={{ pt: 3, pr: 3, pl: 3, pb: 2, bgcolor: 'rgba(255,255,255,0.3)', overflowX: 'hidden', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {valueChainInfo && (
            <Tooltip 
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                  {valueChainInfo}
                </Typography>
              } 
              arrow 
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'info.main',
                    color: 'info.contrastText',
                    '& .MuiTooltip-arrow': {
                      color: 'info.main',
                    },
                    maxWidth: 420,
                    p: 1.5
                  }
                }
              }}
            >
              <IconButton size="small" sx={{ color: 'info.main' }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {valueChainShortcomings && (
            <Tooltip 
              title={
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>
                  {valueChainShortcomings}
                </Typography>
              } 
              arrow 
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    '& .MuiTooltip-arrow': {
                      color: 'warning.main',
                    },
                    maxWidth: 420,
                    p: 1.5
                  }
                }
              }}
            >
              <IconButton size="small" sx={{ color: 'warning.main' }}>
                <WarningAmberIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ height, width: '100%' }}>
        {renderContent()}
      </Box>
      {legend && (
        <Box sx={{ mt: 1 }}>
          {legend}
        </Box>
      )}
    </Paper>
  );
};

export default SankeyDiagram;
